import { useEffect, useMemo, useRef } from 'react';
import {
  computeProjectCosting, projectTrust, overheadRate, equipmentRates as equipmentRatesOf, missingAsked, cfoView, budgetActuals, budgetAlertsDue,
  buildBudgetNotice, buildBudgetGroupNotice, budgetFigure, setupCheck, localToday, tagIndex, commands, agentUids, extraRateFor, extrasOf, planStartFor, projectDashboard,
} from '@battleops/shared';
import { useTasks } from '../context/TaskContext';
import { useSections } from '../context/SectionContext';
import { useDepartments } from '../context/DepartmentContext';
import { useSession } from '../context/SessionContext';
import { useLocalCollection, useLocalDoc } from './useLocal';
import { useProjects } from '../context/ProjectContext';
import { useMetricCtx } from './useMetrics';
import { commitWrites } from './commit';
import { showToast } from './toast';
import { platform } from '../platform';
import { call } from '../session/session';

// What each hour costs and makes, per project (CFO plan phase 1) — the phone's and the web's numbers.
// Pay per person comes from the books (`pay` = opsPay/{uid}); the job's money from the linked books job.

/** opsRates/billRoles (admins; [] for everyone else). */
function useBillRolesList() {
  const doc = useLocalDoc('rates', 'billRoles');
  return useMemo(() => (Array.isArray(doc?.roles) ? doc.roles : []), [doc]);
}

/** What an hour of extra bills at on this project, for whoever's asking: extraRateFor with their pay and the roles. */
export function useExtraRate(project) {
  const pay = usePayMap();
  const roles = useBillRolesList();
  const { identity } = useSession() || {};
  return useMemo(() => extraRateFor(project, pay[identity?.uid] || null, roles), [project?.extraRate, project?.hourlyRate, pay, roles, identity?.uid]);
}

/** One project's dashboard (shared projectDashboard): the costing the job cost screen uses, the job, the type, the extras rate. */
export function useProjectDash(project) {
  const { tasks, allClockSessions, clockSessions } = useTasks();
  const { sections } = useSections();
  const { typeForProject } = useDepartments();
  const costing = useProjectProfit();
  const pay = usePayMap();
  const roles = useBillRolesList();
  const { identity } = useSession() || {};
  const receipts = useLocalCollection('fieldReceipts');
  const expenses = useLocalCollection('expenses');
  const names = useMemberNames();
  const vehicle = useLocalDoc('meta', 'vehicle');
  const type = project ? typeForProject(project.id) : null;
  return useMemo(() => {
    if (!project) return null;
    const cost = costing(project);
    return projectDashboard(project, { tasks, sessions: allClockSessions || clockSessions || [], sections: sections || [], job: cost?.job || null, cost, receipts, expenses, names, me: identity?.uid || null, vehicle, type, pay: pay[identity?.uid] || null, roles, policy: null, now: Date.now() });
  }, [project, tasks, allClockSessions, clockSessions, sections, costing, receipts, expenses, names, vehicle, type, pay, roles, identity?.uid]);
}

/** A job's extras (shared extrasOf) with the signed-in person's rate. */
export function useExtras(project) {
  const { tasks, allClockSessions, clockSessions } = useTasks();
  const pay = usePayMap();
  const roles = useBillRolesList();
  const { identity } = useSession() || {};
  return useMemo(() => (project ? extrasOf(tasks, project, { pay: pay[identity?.uid] || null, roles, planAt: planStartFor(project, allClockSessions || clockSessions || [], tasks) }) : null), [project, tasks, pay, roles, identity?.uid, allClockSessions, clockSessions]);
}

/** { [uid]: opsPay doc } — yours, or the whole team's for an admin. */
export function usePayMap() {
  const rows = useLocalCollection('pay');
  return useMemo(() => Object.fromEntries(rows.map((r) => [r.uid || r.id, r])), [rows]);
}

/**
 * Overhead per billable hour (phase 2): the books' pool (opsRates/overhead — owners only) ÷ billable hours clocked
 * here. → { rates, rate } — both null for a member who can't see overhead.
 */
export function useOverhead() {
  const rates = useLocalDoc('rates', 'overhead');
  const { allClockSessions, clockSessions } = useTasks();
  const { projects } = useProjects();
  const { rawDepartments } = useDepartments();
  const jobs = useLocalCollection('jobs');
  const pay = usePayMap();
  const members = useLocalCollection('members');
  const { identity } = useSession() || {};
  const rate = useMemo(() => overheadRate({ rates, sessions: allClockSessions || clockSessions || [], projects, jobs, departments: rawDepartments || [], pay, me: identity?.uid || null, agents: agentUids(members) }),
    [rates, allClockSessions, clockSessions, projects, jobs, rawDepartments, pay, identity?.uid, members]);
  return { rates, rate };
}

/** Equipment cost per km / hour (phase 3) from the books' opsEquipment docs and the use clocked here (owners only). */
export function useEquipmentRates() {
  const equipment = useLocalCollection('equipment');
  const { allClockSessions, clockSessions } = useTasks();
  const { projects } = useProjects();
  return useMemo(() => (equipment.length ? equipmentRatesOf({ equipment, sessions: allClockSessions || clockSessions || [], projects }) : null),
    [equipment, allClockSessions, clockSessions, projects]);
}

/** { [uid]: display name } for the team (you included). */
export function useMemberNames() {
  const members = useLocalCollection('members');
  const { identity } = useSession() || {};
  return useMemo(() => {
    const out = Object.fromEntries(members.map((m) => [m.uid || m.id, m.name || 'Teammate']));
    if (identity?.uid) out[identity.uid] = identity.name || out[identity.uid] || 'You';
    return out;
  }, [members, identity]);
}

/**
 * (project) → computeProjectCosting(...) with its `cfo` block (both versions) and `trust` (what's missing).
 * Cached per project for the render; recomputes as tasks, sessions, jobs or pay change.
 */
export function useProjectProfit() {
  const billRoles = useBillRolesList();
  const { tasks, allClockSessions, clockSessions } = useTasks();
  const { sections } = useSections();
  const { deptForProject } = useDepartments();
  const { identity } = useSession() || {};
  const expenses = useLocalCollection('expenses');
  const jobs = useLocalCollection('jobs');
  const pay = usePayMap();
  const names = useMemberNames();
  const { rates, rate } = useOverhead();
  const jobPays = useLocalCollection('jobPay');
  const equipmentRates = useEquipmentRates();
  const settings = useLocalDoc('meta', 'settings');
  const weekStart = Number.isInteger(settings?.payWeekStart) ? settings.payWeekStart : 0;
  const me = identity?.uid || null;
  const metricDefs = useLocalCollection('metrics');          // CFO 4.4: asked values never entered go on the trust meter
  const metricValues = useLocalCollection('metricValues');
  return useMemo(() => {
    const jobById = new Map(jobs.map((j) => [j.id, j]));
    const cache = new Map();
    return (project) => {
      if (!project) return null;
      if (!cache.has(project.id)) {
        const job = project.bookProjectId ? jobById.get(project.bookProjectId) || null : null;
        const costing = computeProjectCosting({
          project, tasks, sessions: allClockSessions || clockSessions || [], expenses: expenses || [],
          sections: (sections || []).filter((s) => s.projectId === project.id),
          pay, billRoles, job, dept: deptForProject(project.id), me, now: Date.now(), overhead: rate,
          jobPays, policy: rates?.policy || null, weekStart, equipmentRates,
        });
        const missingMetrics = metricDefs.length ? missingAsked({ tasks, defs: metricDefs, values: metricValues, projectId: project.id }) : [];
        cache.set(project.id, { ...costing, job, trust: projectTrust({ project, costing, job, names, rates, missingMetrics }) });
      }
      return cache.get(project.id);
    };
  }, [tasks, allClockSessions, clockSessions, expenses, sections, jobs, pay, billRoles, names, deptForProject, me, rate, rates, jobPays, weekStart, equipmentRates, metricDefs, metricValues]);
}

// ── the CFO view (CFO plan 5.2–5.5, owners only) ──────────────────────────────────────────────────────────────────

/**
 * The books' budget rows (opsCfo/now.budgets) with BattleOps' hours and metric actuals counted in (budgetActuals).
 * Empty for a member: the CFO doc reaches owners only.
 */
export function useBudgets() {
  const cfo = useLocalDoc('cfo', 'now');
  const { tasks, allClockSessions, clockSessions } = useTasks();
  const { projects } = useProjects();
  const { rawDepartments } = useDepartments();
  const jobs = useLocalCollection('jobs');
  const members = useLocalCollection('members');
  const tags = useLocalCollection('tags');
  const metrics = useLocalCollection('metrics');
  const metricValues = useLocalCollection('metricValues');
  const metricCtx = useMetricCtx();
  const { identity } = useSession() || {};
  return useMemo(() => {
    if (!cfo?.budgets?.length) return [];
    return budgetActuals(cfo.budgets, { sessions: allClockSessions || clockSessions || [], projects, tasks, jobs, departments: rawDepartments || [], tagIndex: tagIndex(tags), agents: agentUids(members), me: identity?.uid || null, now: Date.now(), metrics, metricValues, metricCtx }, localToday());
  }, [cfo, allClockSessions, clockSessions, projects, tasks, jobs, rawDepartments, tags, members, identity?.uid, metrics, metricValues, metricCtx]);
}

/** The whole CFO view (shared cfoView) — the web page, the phone screen. → { cfo, rates, view, budgets, settings } */
export function useCfo() {
  const cfo = useLocalDoc('cfo', 'now');
  const rates = useLocalDoc('rates', 'overhead');
  const settings = useLocalDoc('meta', 'settings');
  const { tasks, allClockSessions, clockSessions } = useTasks();
  const { projects } = useProjects();
  const { rawDepartments } = useDepartments();
  const jobs = useLocalCollection('jobs');
  const members = useLocalCollection('members');
  const expenses = useLocalCollection('expenses');
  const receipts = useLocalCollection('fieldReceipts');
  const jobPays = useLocalCollection('jobPay');
  const tags = useLocalCollection('tags');
  const pay = usePayMap();
  const budgets = useBudgets();
  const { identity } = useSession() || {};
  const view = useMemo(() => cfoView({
    cfo, rates, sessions: allClockSessions || clockSessions || [], projects, jobs, pay, budgets, departments: rawDepartments || [], tasks,
    members, expenses, receipts, jobPays, tags, settings, me: identity?.uid || null, now: Date.now(),
  }), [cfo, rates, allClockSessions, clockSessions, projects, jobs, pay, budgets, rawDepartments, tasks, members, expenses, receipts, jobPays, tags, settings, identity?.uid]);
  return { cfo, rates, view, budgets, settings };
}

/**
 * The setup check (CFO 5.6): what the business is missing before the money numbers hold — Setup → "Getting the
 * numbers right", the top of the CFO view. Rows needing the books' numbers appear once they reach this device (owners).
 */
export function useSetupCheck() {
  const cfo = useLocalDoc('cfo', 'now');
  const rates = useLocalDoc('rates', 'overhead');
  const { projects } = useProjects();
  const { rawDepartments } = useDepartments();
  const jobs = useLocalCollection('jobs');
  const members = useLocalCollection('members');
  const projectTypes = useLocalCollection('projectTypes');
  const taskTemplates = useLocalCollection('taskTemplates');
  const pay = usePayMap();
  const { identity } = useSession() || {};
  return useMemo(() => setupCheck({ members, pay, me: identity?.uid || null, projects, jobs, departments: rawDepartments || [], rates, cfo, projectTypes, taskTemplates }),
    [members, pay, identity?.uid, projects, jobs, rawDepartments, rates, cfo, projectTypes, taskTemplates]);
}

/** Budget writes: saved in the books (saveBudget / deleteBudget, owners; needs a connection), planned hours here. */
export const saveBudget = (budget) => call('saveBudget', budget);
export const deleteBudget = (id) => call('deleteBudget', { id });
export const setPlannedBillableHours = (hours) => commitWrites(commands.setPlannedBillableHours({ now: Date.now() }, { hours }).writes);
/** Date an undated billing stage: its milestone's targetDate (a local command; the books republish the forecast). */
export const setStageDate = (milestoneId, date) => commitWrites(commands.setMilestoneDate({ now: Date.now() }, { milestoneId, date }).writes);

const SEEN_KEY = 'ops.cfo.budgetAlerts';
/**
 * Owners: a gentle in-app notice when a budget reaches 80 % / 100 % — once per budget, period and level on this
 * device. Never pushed. Several at once show as one ("3 budgets need a look") with a button to the CFO budgets
 * (`openBudgets`, the shell's navigation). Mount once (web shell, phone app).
 */
export function useBudgetNotices({ openBudgets = null } = {}) {
  const open = useRef(openBudgets);
  open.current = openBudgets;
  const { access } = useSession() || {};
  const rows = useBudgets();
  const metrics = useLocalCollection('metrics');
  useEffect(() => {
    if (access || !rows.length || !rows.some((r) => r.alert != null)) return undefined;
    let gone = false;
    (async () => {
      let seen = [];
      try { seen = JSON.parse((await platform().kv.getItem(SEEN_KEY)) || '[]'); } catch { seen = []; }
      const due = budgetAlertsDue(rows, new Set(seen));
      if (gone || !due.length) return;
      const fmt = (r, v) => budgetFigure(r, v, metrics.find((m) => m.id === r.metricId) || null);
      const group = buildBudgetGroupNotice(due.map((d) => d.row));
      if (group) showToast({ ...group, ms: 12000, ...(open.current ? { action: { label: 'Look', run: () => open.current?.() } } : {}) });
      else for (const d of due) { const n = buildBudgetNotice(d.row, fmt); if (n) showToast({ ...n, ms: 9000 }); }
      try { await platform().kv.setItem(SEEN_KEY, JSON.stringify([...seen, ...due.map((d) => d.key)].slice(-200))); } catch { /* shown again next time */ }
    })();
    return () => { gone = true; };
  }, [access, rows, metrics]);
}
